import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RepositoryContext } from '../../store/RepositoryProvider';
import NavBar from '../../components/NavBar';
import ReportFilterPaginate, { SHOW_MODE } from '../../components/ReportFilterPaginate';
import API from '../../server/api';
import Table, { ALIGN_TABLE } from '../../components/Table';
import InfoDescription from '../../components/InfoDescription';
import Badge, { BADGE_TYPE } from '../../components/Badge';
import DateFormat, { DATE_FORMAT } from '../../components/DateFormat';
import Phone from '../../components/Phone';
import log from '../../utils/logger';
import Modal from '../../components/Modal';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import { LINE_MODE } from '../../components/Field';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../defs/routes';
import RefundRequest from '../../models/refund/RefundRequest';
import Info from '../../components/Info';
import Price from '../../components/Price';
import RefundInfo from './refund_info';
import { PaymentMethod, PaymentTransaction, PayToPhoneMethod, RefundRequestStatus } from '../../models/types';
import { notify } from '../../components/Toast';
import { nfcRefund, attachAndroidEvent, removeAndroidEvent } from '../../utils/events';
import PaymentSuccess from '../order/payment_success';
import PaymentFail from '../order/payment_fail';
import ReportParams from '../../models/report/ReportParams';

const ORDER_STATUS = {
  pending: BADGE_TYPE.WARN,
  approved: BADGE_TYPE.WARN,
  rejected: BADGE_TYPE.ERROR,
  completed: BADGE_TYPE.SUCCESS
};

const Refunds = () => {
  const {t} = useTranslation(['common', 'refund']);
  const navigate = useNavigate();

  const repository = useContext(RepositoryContext);

  const [showRefundInfo, setShowRefundInfo] = useState(false);
  const [refund, setRefund] = useState(null);
  const refundTask = useRef({
    id: null,
    amount: null,
    refund: null
  });


  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);


  const [showPaymentFail, setShowPaymentFail] = useState(false);
  const handlePaymentFail = (message) => {
    setPaymentMessage(message);
    setShowPaymentFail(true);
  }
  const handlePaymentFailClose = () => setShowPaymentFail(false);


  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const handlePaymentSuccess = (amount) => {
    setPaymentAmount(amount);
    setShowPaymentSuccess(true);
  }
  const handlePaymentSuccessClose = () => {
    setShowPaymentSuccess(false);
  };



  const handleCloseOrderInfo = useCallback(() => {
    setShowRefundInfo(false);
    setRefund(null);
  }, []);

  const handleOpenRefundInfo = useCallback((refundId) => {
    repository.action(API.cashier.refundRequest, {id: refundId}).then((refund) => {
      repository.action(API.order.order, {id: refund.order.id}).then((order) => {
        refund.order = order;
        setRefund(refund);
        setShowRefundInfo(true);
      }).catch((e) => {
        log.error(e);
        notify.error(t('refund:error.refund_not_load'));
      })
    }).catch((e) => {
      log.error(e);
      notify.error(t('refund:error.refund_not_load'));
    })
  }, []);

  const handleOpenNewRefund = () => {
    navigate(ROUTES.NEW_REFUND);
  };

  const autocompleteHandler = async (query) => {
    return repository.list(API.cashier.refundRequests, {}, Object).then((rows) => {
      let list = [];
      if (rows) {
        for (let i = 0; i < rows.length; ++i) {
          list.push({
            value: rows[i].value,
            title: `${rows[i].label}. ${t('refund:refunds.phone', { phone: Phone.Text({ phone: rows[i].meta.phone }) })}`
          });
        }
      }
      return list;
    }).catch((e) => {
      log.error(e);
      return Promise.reject(e);
    });
  };

  const handlerRefunds = async (params) => {
    return repository.report(API.cashier.refundRequestsReport, new ReportParams(params), RefundRequest).then((report) => {
      return report;
    }).catch((e) => {
      log.error(e);
      return Promise.reject(e);
    });
  };

  const handleRefund = (refund, id, amount) => {
    refundTask.current.id = id;
    refundTask.current.amount = amount;
    refundTask.current.refund = refund;

    if (refund.method === PaymentMethod.CARD || refund.method === PaymentMethod.QRCODE) {
      repository.action(API.order.order, {id: refund.order.id}).then((order) => {
        let payment = order.paymentDetails.payments.filter((p) => p.type === 'income' && [PaymentMethod.CARD, PaymentMethod.QRCODE].includes(p.method));
        if (payment[0]) {
          nfcRefund(`${amount}`, payment[0].meta.method ? payment[0].meta.method.toUpperCase() : payment[0].method === 'card' ? PayToPhoneMethod.NFC : PayToPhoneMethod.QR, `${payment[0].meta.rrn}`, `${payment[0].meta.mid}`).catch((e) => {
            log.error(e);
          });
        } else {
          handlePaymentFail(t('refund:refunds.invalid_data'));
        }
      });
    } else {
     // completeRefundRequest(refund, id, amount);
    }
  };

  const completeRefundRequest = (refund, id, amount) => {
    repository.action(API.cashier.completeRefundRequest, { id, amount }).then(() => {
      refund.status = 'completed';
      handlePaymentSuccess(amount);
    }).catch((e) => {
      if (e.error === 'FORM_ERROR' && e.fields && Object.keys(e.fields).find((f) => f.match('actualPrice'))) {
        handlePaymentFail(t('refund:error.invalid_amount'));
      } else {
        handlePaymentFail(t('refund:error.invalid_data'));
      }
    });
  };

  useEffect(() => {
    attachAndroidEvent('refund', async (e) => {
      const paymentData = new PaymentTransaction(e.detail);
      if (paymentData.error) {
        let message = paymentData.error;
        if (message.toLowerCase().indexOf('Failed to bind to Pay to phone'.toLowerCase()) >= 0) {
          message = t('common:error.pay_to_phone_app_no_launch');
          message += t('common:pay_to_phone_app_no_launch.todo');
          message += t('common:pay_to_phone_app_no_launch.step1');
          if (paymentData.manufacturer && paymentData.manufacturer.toLowerCase() === 'tecno') {
            message += t('common:pay_to_phone_app_no_launch.step2_tecno');
          } else {
            message += t('common:pay_to_phone_app_no_launch.step2_other');
          }
        }

        handlePaymentFail(message);
      } else {
        completeRefundRequest(refundTask.current.refund, refundTask.current.id, refundTask.current.amount);
      }
    });

    return () => {
      removeAndroidEvent('refund');
    };
  }, []);

  return (
    <div className="trip-app">
      <NavBar title={t('refund:refunds.title')} />
      <div className="trip-app-body">
        <Button.Panel>
            <Button position={BUTTON_POSITION.LEFT} onClick={handleOpenNewRefund}>{t('refund:button.create_refund')}</Button>
        </Button.Panel>
        <ReportFilterPaginate
          apiHandler={(reportParams) => handlerRefunds(reportParams)}
          show={SHOW_MODE.ALWAYS}
          // autosuggest={{
          //   key: 'client',
          //   apiHandler: autocompleteHandler
          // }}
          sort={(a, b) => a.date < b.date ? 1 : (a.date > b.date ? -1 : 0)}
          placeholder={t('refund:refunds.filter')}
          output={(list) => (
            <Table cols={3}>
              {list.map((refund) => (
                <Fragment key={refund.id}>
                  <Table.Row onClick={() => { handleOpenRefundInfo(refund.id); }}>
                    <Table.Cell colspan={2}>
                      <InfoDescription description={<>{t('refund:refunds.from_date')}<DateFormat.DateTime date={refund.initialDate}/></>}>
                          {t('refund:refunds.refund', { refund: refund.number })}
                          <span className="ms-2 opacity-50">
                              ( {t('refund:refunds.order', { order: refund.order.number })} )
                          </span>
                      </InfoDescription>
                    </Table.Cell>
                    <Table.Cell align={ALIGN_TABLE.RIGHT}>
                      <Badge type={ORDER_STATUS[refund.status]}>{t(`refund:refund_status.${refund.status}`)}</Badge>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="pb-0" onClick={() => { handleOpenRefundInfo(refund.id); }}>
                    <Table.Cell className="small">
                      {t('refund:refunds.refund_amount')}
                    </Table.Cell>
                    <Table.Cell colspan={2} className="small" align={ALIGN_TABLE.RIGHT}>
                      <Info><Price value={refund.amount}/> {t('refund:refunds.of')} <Price value={refund.order.paymentDetails.partialValue}/></Info>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="pb-0">
                    <Table.Cell className="small">
                      {t('refund:refunds.refund_method')}
                    </Table.Cell>
                    <Table.Cell colspan={2} className="small" align={ALIGN_TABLE.RIGHT}>
                      <Info>{t(`common:payment_method.${refund.method}`)}</Info>
                    </Table.Cell>
                  </Table.Row>
                  {refund.status === RefundRequestStatus.APPROVED && (refund.method === PaymentMethod.CARD || refund.method === PaymentMethod.QRCODE) && (
                    <Table.Row className="pb-0">
                      <Table.Cell colspan={2} className="small" align={ALIGN_TABLE.RIGHT}>
                        <Button.Panel>
                          <Button variant={BUTTON_TYPE.PRIMARY} onClick={() => handleRefund(refund, refund.id, refund.amount)}>{t('refund:refunds.refund_money')}</Button>
                        </Button.Panel>
                      </Table.Cell>
                    </Table.Row>
                  )}
                  <Table.Row delim={true} className="mb-1" />
                </Fragment>
              ))}
            </Table>
          )}
          filters={[
            // { key: 'order.number', title: t('refund:filter.order_number'), type: 'value.text', mode: LINE_MODE.TWO },
            { key: 'number', title: t('refund:filter.refund_number'), type: 'value.text', mode: LINE_MODE.TWO },
            //{ key: 'createdAt', title: t('refund:filter.initial_date'), type: 'value.date', mode: LINE_MODE.TWO, erase: true, value: null },
            // { key: 'status', title: t('sale:filter.status'), type: 'badge', list: [
            //   { id: OrderStatus.AWAITING_PAYMENT, title: t('sale:status.awaiting_payment') },
            //   { id: OrderStatus.PAID, title: t('sale:status.paid') },
            //   { id: OrderStatus.CANCELLED, title: t('sale:status.cancelled') },
            //   { id: OrderStatus.COMPLETED, title: t('sale:status.completed') }
            // ] },
          ]}
        />
      </div>

      <Modal show={refund && showRefundInfo} onHide={handleCloseOrderInfo}>
        <Modal.Header closeButton>
          {showRefundInfo && (
            <InfoDescription
              description={(
                <>
                  {t('refund:refund_info.create')}
                  <DateFormat.DateTime date={refund && refund.initialDate}/>
                </>
              )}
            >
              {t('refund:refunds.refund', { refund: refund && refund.number })}
            </InfoDescription>
          )}
        </Modal.Header>
        <Modal.Body>
          {showRefundInfo && ( /* re-create body*/
            <RefundInfo refund={refund} onNext={handleCloseOrderInfo} onClose={handleCloseOrderInfo} />
          )}
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>

      <Modal show={showPaymentFail} onHide={handlePaymentFailClose}>
        <Modal.Header>{t('refund:refunds.refund', { refund: refund && refund.number })}</Modal.Header>
        <Modal.Body>
          <PaymentFail message={paymentMessage} />
        </Modal.Body>
        <Modal.Footer>
          <Button.Panel className="button-panel-center">
            <Button position={BUTTON_POSITION.CENTER} variant={BUTTON_TYPE.PRIMARY} onClick={handlePaymentFailClose}>{t('common:button.ok')}</Button>
          </Button.Panel>
        </Modal.Footer>
      </Modal>

      <Modal show={showPaymentSuccess} onHide={handlePaymentSuccessClose}>
        <Modal.Header>{t('refund:refunds.refund', { refund: refund && refund.number })}</Modal.Header>
        <Modal.Body>
          <PaymentSuccess amount={paymentAmount} />
        </Modal.Body>
        <Modal.Footer>
          <Button.Panel className="button-panel-center">
            <Button position={BUTTON_POSITION.CENTER} variant={BUTTON_TYPE.PRIMARY} onClick={handlePaymentSuccessClose}>{t('common:button.ok')}</Button>
          </Button.Panel>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default Refunds;